import React from 'react';
import {
  Button,
  Dialog,
  DialogTrigger,
  DocsAnnotation,
  Flex,
  ScreenshotCarousel,
  StatusIndicator,
  Text,
  Tooltip,
  TooltipTrigger,
  VideoPlayerWithTimeline,
  WaitCountdown,
  useCountdown,
} from '@chatsdet/ui';
import { ArrowLeft, ArrowRight, ArrowSquareOut, FilmStrip } from '@phosphor-icons/react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { VideoTimeline } from '@chatsdet/types';
import { API_URL } from '../../../../config';
import { runStatusLabel, statusVariant } from './run-formatters';
import { RecordedWindow } from '../../../components/RecordedWindow';
import type { WorkflowRunWithStepsDto } from '../hooks/useWorkflowRun';
import {
  useWorkflowRunThoughts,
  calculateThoughtTimestamps,
} from '../hooks/useWorkflowRunThoughts';
import { useWorkflowRunDiagnostics } from '../../../../hooks/useWorkflowRunDiagnostics';
import { useWorkflowRunTokenUsage } from '../../../../hooks/useWorkflowRunTokenUsage';
import type { WorkflowNode } from '../../../../types/workflow';
import { useAuth } from '../../../../contexts/AuthContext';
import { SuperadminOnly } from '../../../components/SuperadminOnly';
import { useWorkflowRunClip } from './useWorkflowRunClip';
import { AssertionSummaryCard } from './StepAssertions';
import { RunVariablesPanel } from './RunVariablesPanel';
import {
  hasUnavailableRecoveredStepHistory,
  RunStepList,
  nodeLabelWithStepsFallback,
} from './RunStepList';
import { CodeFlowRunSteps } from '../../../../features/code-flows';
import { RunFailureHeadline, findFailedRunStep, getFailedStepData } from './RunFailureHeadline';
import { useRunFailureDiagnostic } from '../../../../hooks/useRunFailureDiagnostic';
import { useAssertionIssueLinkContext } from '../../../../hooks/useAssertionIssueLinkContext';
import { RunWaterfallView } from './RunWaterfallView';
import { RunDebugDrawer } from './debug/RunDebugDrawer';
import { useRunSnapshotNodes } from './useRunSnapshotNodes';
import type { SerializedTrackedVariableDto } from '../hooks/useWorkflowRun';

export function RunDetailsPanel({
  run,
  nodes: liveNodes,
  onBack,
  headerRight,
  onOpenCacheDebug: _onOpenCacheDebug,
  propertyId,
}: {
  run: WorkflowRunWithStepsDto;
  nodes: WorkflowNode[];
  onBack: () => void;
  headerRight?: React.ReactNode;
  onOpenCacheDebug?: (runId: string) => void;
  /** Powers "Link issue" → file-new-issue mode (see `AssertionIssueLinkContext`). */
  propertyId: string | null;
}) {
  // Prefer the run-pinned snapshot's nodes (execution-time truth) over the
  // live definition, which may have been edited since this run.
  const { nodes, fromSnapshot: nodesFromSnapshot } = useRunSnapshotNodes(
    run.workflowId,
    run,
    liveNodes
  );
  // URL-driven expanded video state
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { workflowId?: string };
  const searchParams = useSearch({ strict: false }) as { expandedVideo?: boolean };
  const expandedVideo = searchParams.expandedVideo === true;

  const handleFullscreenChange = React.useCallback(
    (open: boolean) => {
      if (!params.workflowId) return;
      navigate({
        to: '/flows/$workflowId',
        params: { workflowId: params.workflowId },
        search: (prev: Record<string, unknown>) => {
          const next = { ...prev };
          if (open) {
            next.expandedVideo = true;
          } else {
            delete next.expandedVideo;
          }
          return next;
        },
        replace: true,
      });
    },
    [navigate, params.workflowId]
  );

  // State for lazy loading diagnostics (enabled when user opens Console/Network tab)
  const [loadDiagnostics, setLoadDiagnostics] = React.useState(false);

  // Waterfall dialog state (superadmin only)
  const [waterfallOpen, setWaterfallOpen] = React.useState(false);

  // Fetch thoughts for this run
  const { data: thoughts = [] } = useWorkflowRunThoughts(run.workflowId, run.id);

  // Fetch diagnostics (lazy loaded)
  const diagnosticsQuery = useWorkflowRunDiagnostics(run.workflowId, run.id, loadDiagnostics);

  // Fetch failure diagnostic analysis (drives the inline failure banner below)
  const failureDiagnostic = useRunFailureDiagnostic(
    run.workflowId,
    run.id,
    run.status === 'failed'
  );

  // Open issues linked to this workflow — resolves an already-linked
  // assertion to its issue, and is the candidate list for "Attach to open
  // issue" in the Link issue dialog. Filing a *new* issue additionally needs
  // a primary URL (see `useAssertionIssueLinkContext`).
  const issueLinkContext = useAssertionIssueLinkContext(run, run.workflowId, propertyId);

  // State for showing thoughts in video player
  const [showThoughtsInVideo, setShowThoughtsInVideo] = React.useState(true);

  // Version label for the header pill — memoized since it's rendered twice
  // (truncated pill + tooltip).
  const versionLabel = React.useMemo(() => {
    if (run.workflowRevision) return `Revision ${run.workflowRevision.number}`;
    if (run.snapshotVersion) return `v${run.snapshotVersion}`;
    if (run.draftSnapshot) {
      return run.draftSnapshot.basePublishedVersion > 0
        ? `v${run.draftSnapshot.basePublishedVersion}.${run.draftSnapshot.version}`
        : `v0.${run.draftSnapshot.version}`;
    }
    return '';
  }, [run.workflowRevision, run.snapshotVersion, run.draftSnapshot]);

  // Calculate timestamped thoughts for video synchronization
  const timeline = (run.videoTimelineJson as VideoTimeline) ?? null;
  const thoughtsWithTimestamps = React.useMemo(
    () => calculateThoughtTimestamps(thoughts, timeline),
    [thoughts, timeline]
  );

  // Build step metadata map for cache/agent badges in video timeline
  const stepMetadata = React.useMemo(() => {
    const map: Record<
      string,
      {
        cacheOutcome?: string;
        cacheHitSource?: string;
        cacheOutcomeReason?: string;
        waitShortcut?: boolean;
      }
    > = {};
    for (const step of run.steps) {
      if (step.metadataJson?.cacheOutcome) {
        map[step.nodeId] = {
          cacheOutcome: step.metadataJson.cacheOutcome as string | undefined,
          cacheHitSource: step.metadataJson.cacheHitSource as string | undefined,
          cacheOutcomeReason: step.metadataJson.cacheOutcomeReason as string | undefined,
          waitShortcut: step.metadataJson.waitShortcut as boolean | undefined,
        };
      }
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [run.steps]);

  // Find the failed step (if any) — exclude warn-severity assertion steps
  const failedStep = React.useMemo(() => {
    const step = findFailedRunStep(run.steps);
    if (!step) return null;
    return getFailedStepData(step, nodes, run.steps);
  }, [run.steps, nodes]);

  const screenshotItems = React.useMemo(() => {
    const items = run.steps
      .filter((step) => typeof step.screenshotS3Key === 'string' && step.screenshotS3Key.length > 0)
      .map((step) => {
        const encoded = encodeURIComponent(step.screenshotS3Key as string);
        const src = `${API_URL}/screenshots/${encoded}`;
        return {
          id: step.id,
          src,
          caption: nodeLabelWithStepsFallback(nodes, step.nodeId, run.steps),
        };
      });
    return items;
  }, [run.steps, run.orgId, nodes]);

  // Find waiting step and extract countdown info
  // Check both step.metadataJson and run.metadataJson for resumeAt
  const waitingStepInfo = React.useMemo(() => {
    if (
      run.status === 'failed' ||
      run.status === 'inconclusive' ||
      run.status === 'success' ||
      run.status === 'canceled'
    ) {
      return null;
    }
    // Only treat as waiting if the latest step is itself waiting — a stale
    // 'waiting' status on an earlier step must not block the video/screenshots.
    const lastStep = run.steps[run.steps.length - 1];
    if (!lastStep || lastStep.status !== 'waiting') return null;
    const waitingStep = lastStep;

    // First check step-level metadata
    const stepMetadata = waitingStep.metadataJson ?? {};
    let resumeAt = typeof stepMetadata.resumeAt === 'string' ? stepMetadata.resumeAt : null;
    let delaySeconds =
      typeof stepMetadata.delaySeconds === 'number' ? stepMetadata.delaySeconds : undefined;

    // Fallback to run-level metadata if step doesn't have resumeAt
    if (!resumeAt && run.metadataJson) {
      const runMetadata = run.metadataJson as Record<string, unknown>;
      if (typeof runMetadata.resumeAt === 'string') {
        resumeAt = runMetadata.resumeAt;
      }
      if (delaySeconds === undefined && typeof runMetadata.delaySeconds === 'number') {
        delaySeconds = runMetadata.delaySeconds;
      }
    }

    if (!resumeAt) return null;

    return {
      stepId: waitingStep.id,
      nodeId: waitingStep.nodeId,
      nodeName: nodeLabelWithStepsFallback(nodes, waitingStep.nodeId, run.steps),
      resumeAt,
      delaySeconds,
    };
  }, [run.steps, run.metadataJson, nodes]);

  // Countdown hook for waiting state
  const countdown = useCountdown(waitingStepInfo?.resumeAt, waitingStepInfo?.delaySeconds);

  // Release clip (superadmin only)
  const clipHook = useWorkflowRunClip(run.workflowId, run.id, !!run.videoStorageKey);

  // Tokens + cost (superadmin only)
  const { user } = useAuth();
  const tokenUsageQuery = useWorkflowRunTokenUsage(run.id, run.orgId, !!user?.isSuperadmin);

  return (
    <DocsAnnotation slug="reference/runs" title="Run Details">
      <div className="flex flex-col h-full">
        {/* Header. `px-4` lives here (and on the scroll body below) rather
            than on the sidebar's tab wrapper, which is now gutterless so the
            run *list* can be full-bleed. Detail content is inset content. */}
        <div className="px-4 py-3 border-b border-border space-y-1.5">
          <Flex justify="between" align="center" gap="sm">
            <Flex align="center" gap="sm">
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Back to Runs"
                aria-label="Back to Runs"
                onPress={onBack}
                className="shrink-0"
              >
                <ArrowLeft size={16} />
              </Button>

              {/* Status */}
              <StatusIndicator
                variant={statusVariant[run.status]}
                size="sm"
                pulse={run.status === 'running'}
              >
                {runStatusLabel(run.status)}
              </StatusIndicator>
            </Flex>

            <Flex align="center" gap="sm">
              {/* `ArrowRight`, not `ArrowSquareOut`: this navigates to an
                  in-app route (/runs/$runId) in the same tab. The
                  external-link glyph promises a new tab / another site and
                  delivers neither. Ghost keeps the header's emphasis on the
                  run's status rather than on a secondary navigation. */}
              <Button
                variant="ghost"
                size="sm"
                iconRight={<ArrowRight size={14} />}
                onPress={() =>
                  navigate({
                    to: '/runs/$runId',
                    params: { runId: run.id },
                  })
                }
                className="shrink-0"
              >
                Full detail
              </Button>
              {headerRight}
            </Flex>
          </Flex>

          {/* Recorded times and duration remain readable; the version string
              is the element that shrinks/truncates under space pressure. */}
          {/* No `pl-8` indent: the meta row starts on the header's own gutter,
              so its left edge lines up with the back button above it and its
              right edge with that row's action button. */}
          <Flex align="center" gap="sm">
            {(run.workflowRevision || run.snapshotVersion || run.draftSnapshot) && (
              <>
                <TooltipTrigger delay={0}>
                  <Text
                    size="xs"
                    className="min-w-0 shrink truncate rounded bg-muted px-1.5 py-0.5 font-mono text-muted-foreground cursor-default"
                  >
                    {versionLabel}
                  </Text>
                  <Tooltip>{versionLabel}</Tooltip>
                </TooltipTrigger>
                <Text size="xs" className="shrink-0 text-muted-foreground">
                  •
                </Text>
              </>
            )}

            <Text size="xs" className="min-w-0 text-muted-foreground">
              <RecordedWindow
                startedAt={run.startedAt}
                finishedAt={run.finishedAt}
                isRunning={run.status === 'running'}
              />
            </Text>
          </Flex>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Failure banner - shown when run failed */}
          {run.status === 'failed' && (
            <RunFailureHeadline
              failedStep={failedStep}
              runErrorMessage={run.errorMessage}
              action={
                failedStep
                  ? {
                      label: 'View details',
                      onPress: () =>
                        navigate({
                          to: '/runs/$runId',
                          params: { runId: run.id },
                        }),
                    }
                  : undefined
              }
            />
          )}
          {run.status === 'inconclusive' && (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
              <Text size="sm" weight="medium">
                Platform error — the run could not reach a verdict. No test result was recorded.
              </Text>
            </div>
          )}

          {/* Wait countdown panel - shown when run has a waiting step */}
          {waitingStepInfo && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 p-6">
              <div className="flex flex-col items-center">
                <WaitCountdown
                  formatted={countdown.formatted}
                  progress={countdown.progress}
                  isComplete={countdown.isComplete}
                  isResuming={countdown.isResuming}
                  size="lg"
                />
                <Text size="sm" color="muted" className="mt-4 text-center">
                  Workflow will resume automatically
                </Text>
              </div>
            </div>
          )}

          {/* Video or screenshots - no headings. */}
          {!waitingStepInfo && run.videoStorageKey ? (
            <VideoPlayerWithTimeline
              posterSrc={screenshotItems[0]?.src ?? null}
              videoSrc={`${API_URL}/videos/${encodeURIComponent(run.videoStorageKey)}`}
              timeline={timeline}
              thoughts={thoughtsWithTimestamps}
              showThoughts={showThoughtsInVideo}
              onThoughtsVisibilityChange={setShowThoughtsInVideo}
              alt="Workflow run recording"
              consoleLogs={diagnosticsQuery.data?.consoleLogs}
              networkRequests={diagnosticsQuery.data?.networkRequests}
              videoStartedAt={diagnosticsQuery.data?.videoStartedAt}
              diagnosticsLoading={diagnosticsQuery.isLoading}
              onLoadDiagnostics={() => setLoadDiagnostics(true)}
              isFullscreen={expandedVideo}
              onFullscreenChange={handleFullscreenChange}
              stepMetadata={stepMetadata}
            />
          ) : !waitingStepInfo ? (
            <ScreenshotCarousel
              items={screenshotItems}
              emptyLabel={
                run.status === 'running'
                  ? 'Waiting for screenshots...'
                  : run.hasVideoSegments
                    ? 'Video processing...'
                    : 'No screenshots'
              }
              dialogTitle="Workflow Screenshot"
              aspectRatioClass="aspect-video"
            />
          ) : null}

          {/* Run Variables */}
          {(() => {
            const trackedVars = (run.metadataJson as Record<string, unknown> | null)
              ?.trackedVariables;
            if (!Array.isArray(trackedVars) || trackedVars.length === 0) return null;
            return (
              <RunVariablesPanel
                variables={trackedVars as SerializedTrackedVariableDto[]}
                nodes={nodes}
              />
            );
          })()}

          {/* Assertion Summary (non-blocking assertions) */}
          {!!run.metadataJson?.assertionSummary && (
            <AssertionSummaryCard
              summary={
                run.metadataJson
                  .assertionSummary as import('@chatsdet/types').WorkflowAssertionSummary
              }
              results={run.steps.flatMap(
                (s) =>
                  (s.metadataJson?.assertionResults ??
                    []) as import('@chatsdet/types').WorkflowAssertionResult[]
              )}
            />
          )}

          {/* Steps */}
          <div>
            <Text weight="medium" size="sm" className="mb-2">
              Steps
            </Text>
            {run.status === 'failed' && (
              <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
                <Text size="xs" weight="medium" className="text-destructive">
                  {failureDiagnostic.diagnostic?.title ?? run.errorMessage ?? 'Workflow run failed'}
                </Text>
                {failureDiagnostic.status === 'pending' ||
                failureDiagnostic.status === 'running' ? (
                  <Text size="xs" className="mt-1 text-muted-foreground">
                    Analyzing failure…
                  </Text>
                ) : null}
              </div>
            )}
            <CodeFlowRunSteps runId={run.id}>
              <RunStepList
                steps={run.steps}
                nodes={nodes}
                workflowId={run.workflowId}
                runId={run.id}
                nodesFromSnapshot={nodesFromSnapshot}
                stepHistoryUnavailable={hasUnavailableRecoveredStepHistory(
                  run.steps,
                  run.metadataJson
                )}
                issueLinkContext={issueLinkContext ?? undefined}
              />
            </CodeFlowRunSteps>
          </div>

          {/* Release clip — superadmin-only, only renders when a clip exists (no empty header). */}
          {run.videoStorageKey && (
            <SuperadminOnly>
              <Flex
                align="center"
                justify="between"
                className="p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-md"
              >
                <Flex align="center" gap="sm">
                  <FilmStrip size={16} className="text-orange-600 dark:text-orange-400" />
                  <Text size="sm" weight="medium">
                    Release Clip
                  </Text>
                </Flex>
                {clipHook.clip ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={() => window.open(clipHook.clip!.url, '_blank', 'noopener')}
                  >
                    <ArrowSquareOut size={14} className="mr-1" /> Open
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    isDisabled={clipHook.isGenerating}
                    onPress={() => clipHook.generate()}
                  >
                    {clipHook.isGenerating ? 'Generating…' : 'Generate'}
                  </Button>
                )}
              </Flex>
            </SuperadminOnly>
          )}

          {/* Tokens + cost — superadmin-only, only renders once totals load. */}
          {tokenUsageQuery.data && (
            <SuperadminOnly>
              <Flex
                align="center"
                gap="md"
                className="p-3 bg-muted/40 border border-border rounded-md"
              >
                <Text size="sm" className="text-muted-foreground">
                  Tokens:{' '}
                  <Text as="span" weight="medium" className="text-foreground">
                    {(
                      tokenUsageQuery.data.inputTokens + tokenUsageQuery.data.outputTokens
                    ).toLocaleString()}
                  </Text>
                </Text>
                <Text size="sm" className="text-muted-foreground">
                  Cost:{' '}
                  <Text as="span" weight="medium" className="text-foreground">
                    ${(tokenUsageQuery.data.costCents / 100).toFixed(2)}
                  </Text>
                </Text>
              </Flex>
            </SuperadminOnly>
          )}
        </div>

        {/* Waterfall dialog (superadmin-only legacy path; Debug drawer preferred) */}
        <DialogTrigger isOpen={waterfallOpen} onOpenChange={setWaterfallOpen} size="full">
          <span className="hidden" />
          <Dialog title="Step Waterfall" description={`${run.steps.length} steps`}>
            <RunWaterfallView run={run} thoughts={thoughts} nodes={nodes} />
          </Dialog>
        </DialogTrigger>

        {/* Consolidated Debug drawer — driven by ?debug=1 search params */}
        {/* No UI launcher points here any more (the Debug row was removed as
            unused); the drawer still opens from `?debug=true`. Delete this and
            the router's debug search params once that's confirmed dead. */}
        <RunDebugDrawer run={run} nodes={nodes} />
      </div>
    </DocsAnnotation>
  );
}
